import axios from 'axios';
import { errorHandler } from "@/services/errorHandler";

export default {
  namespaced: true,
  state: {
    unpaidInvoices: [],
    paidInvoices: [],
    invoiceDetails: null,
    loading: false,
    dateRange: null,
    includeKazang: 2 // Default to Only Premium (2)
  },
  mutations: {
    SET_UNPAID_INVOICES(state, invoices) {
      state.unpaidInvoices = invoices;
    },
    SET_PAID_INVOICES(state, invoices) {
      state.paidInvoices = invoices;
    },
    SET_INVOICE_DETAILS(state, details) {
      state.invoiceDetails = details;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_DATE_RANGE(state, dateRange) {
      state.dateRange = dateRange;
    },
    SET_INCLUDE_KAZANG(state, includeKazang) {
      state.includeKazang = includeKazang;
    }
  },
  actions: {
    async getUnpaidInvoices({ commit, state }, params = {}) {
      commit('SET_LOADING', true);
      try {
        const includeKazang = params.includeKazang !== undefined ? params.includeKazang : state.includeKazang;
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.subscriptionApi + 'Invoices/getUnpaid', {
          params: { includeKazang, dateRange }
        });

        commit('SET_UNPAID_INVOICES', response.data);
        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getUnpaidInvoicesByProduct({ commit, state }, { productId, params = {} }) {
      commit('SET_LOADING', true);
      try {
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.subscriptionApi + 'Invoices/getUnpaidByProduct', {
          params: { productId, ...dateRange }
        });

        commit('SET_UNPAID_INVOICES', response.data);
        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getPaidInvoices({ commit, state }, params = {}) {
      commit('SET_LOADING', true);
      try {
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.subscriptionApi + 'Invoices/getPaid', {
          params: { ...dateRange }
        });

        commit('SET_PAID_INVOICES', response.data);
        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getPaidInvoicesByProduct({ commit, state }, { productId, params = {} }) {
      commit('SET_LOADING', true);
      try {
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.subscriptionApi + 'Invoices/getPaidByProduct', {
          params: { productId, ...dateRange }
        });

        commit('SET_PAID_INVOICES', response.data);
        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getInvoiceById({ commit }, id) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.get(window.subscriptionApi + 'Invoices/getByID', {
          params: { id }
        });

        commit('SET_INVOICE_DETAILS', response.data);
        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return null;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getPaidInvoicesByCustomer({ commit }, customerId) {
      commit('SET_LOADING', true);
      try {
        if (!customerId) return [];
        const response = await axios.get(window.subscriptionApi + 'Invoices/getPaidByCustomer', {
          params: { customerId }
        });

        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async notifyUnpaidInvoices({ commit, state }, params = {}) {
      commit('SET_LOADING', true);
      try {
        const includeKazang = params.includeKazang !== undefined ? params.includeKazang : state.includeKazang;
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.subscriptionApi + 'Invoices/notifyUnpaid', {
          params: { includeKazang, dateRange }
        });

        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to send notifications' };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async notifyUnpaidInvoicesByProduct({ commit, state }, { productId, params = {} }) {
      commit('SET_LOADING', true);
      try {
        const includeKazang = params.includeKazang !== undefined ? params.includeKazang : state.includeKazang;
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.subscriptionApi + 'Invoices/notifyUnpaidByProduct', {
          params: { productId, includeKazang, dateRange }
        });

        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to send notifications' };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async resendReminder({ commit }, id) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.post(window.subscriptionApi + `adminCustomerPanel/notifyClient/${id}`);
        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to resend reminder' };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async processManualPayment({ commit, dispatch }, paymentData) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.post(window.subscriptionApi + 'paymentCompleted/processManualPayment', paymentData);
        // Refresh the unpaid invoices list after processing payment
        await dispatch('getUnpaidInvoices');
        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to process payment' };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateInvoice({ commit, dispatch }, { id, invoiceData }) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.put(window.subscriptionApi + 'invoice/update', {
          id,
          ...invoiceData
        });

        // Refresh the invoice details
        if (response.data.success) {
          await dispatch('getInvoiceById', id);
        }

        return response.data;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to update invoice' };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async exportUnpaidInvoices({ state }, params = {}) {
      try {
        const includeKazang = params.includeKazang !== undefined ? params.includeKazang : state.includeKazang;
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.laravelApi + 'subscription/exportUnpaidInvoices', {
          params: { includeKazang, dateRange },
          responseType: 'blob'
        });

        // Create a download link and trigger the download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'unpaid_invoices.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return { success: true };
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to export invoices' };
      }
    },

    async exportUnpaidInvoicesByProduct({ state }, { productId, params = {} }) {
      try {
        const includeKazang = params.includeKazang !== undefined ? params.includeKazang : state.includeKazang;
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.subscriptionApi + 'Invoices/exportUnpaidInvoicesByProduct', {
          params: { productId, includeKazang, dateRange },
          responseType: 'blob'
        });

        // Create a download link and trigger the download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'unpaid_invoices_by_product.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return { success: true };
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to export invoices' };
      }
    },

    async exportPaidInvoices({ state }, params = {}) {
      try {
        const includeKazang = params.includeKazang !== undefined ? params.includeKazang : state.includeKazang;
        const dateRange = params.dateRange || state.dateRange;

        const response = await axios.get(window.laravelApi + 'subscription/exportPaidInvoices', {
          params: { includeKazang, dateRange },
          responseType: 'blob'
        });

        // Create a download link and trigger the download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'paid_invoices.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return { success: true };
      } catch (error) {
        await errorHandler.tomcatError(error);
        return { success: false, message: 'Failed to export invoices' };
      }
    },

    setDateRange({ commit }, dateRange) {
      commit('SET_DATE_RANGE', dateRange);
    },

    setIncludeKazang({ commit }, includeKazang) {
      commit('SET_INCLUDE_KAZANG', includeKazang);
    }
  },
  getters: {
    getUnpaidInvoices: state => state.unpaidInvoices,
    getPaidInvoices: state => state.paidInvoices,
    getInvoiceDetails: state => state.invoiceDetails,
    isLoading: state => state.loading,
    getDateRange: state => state.dateRange,
    getIncludeKazang: state => state.includeKazang
  }
};
